import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, FileText, CreditCard, Calendar, MapPin, Phone, Mail, 
  Building2, GraduationCap, Download, Eye, Clock, CheckCircle, 
  AlertCircle, DollarSign
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Datos mockup de cuotas
const cuotasMockup = [
  { id: 1, periodo: 'Enero 2026', monto: 15000, estado: 'pagado', fecha_pago: '2026-01-15', fecha_vencimiento: '2026-01-10' },
  { id: 2, periodo: 'Febrero 2026', monto: 15000, estado: 'pagado', fecha_pago: '2026-02-12', fecha_vencimiento: '2026-02-10' },
  { id: 3, periodo: 'Marzo 2026', monto: 15500, estado: 'pendiente', fecha_pago: null, fecha_vencimiento: '2026-03-10' },
  { id: 4, periodo: 'Abril 2026', monto: 15500, estado: 'pendiente', fecha_pago: null, fecha_vencimiento: '2026-04-10' },
  { id: 5, periodo: 'Mayo 2026', monto: 15500, estado: 'pendiente', fecha_pago: null, fecha_vencimiento: '2026-05-10' },
  { id: 6, periodo: 'Junio 2026', monto: 15500, estado: 'pendiente', fecha_pago: null, fecha_vencimiento: '2026-06-10' },
];

const MiPerfilSocio = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [socioData, setSocioData] = useState(null);

  useEffect(() => {
    fetchSocioData();
  }, []);

  const fetchSocioData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/socios/mi-perfil`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSocioData(response.data);
    } catch (error) {
      console.error('Error fetching socio data:', error);
      // Datos de ejemplo si no hay datos reales
      setSocioData({
        nombre: user?.nombre || 'Nombre del Socio',
        apellido: user?.apellido || '',
        email: user?.email || '',
        categoria_socio: 'Especialista',
        fecha_alta: '2024-03-15',
        matricula_nacional: '123456',
        matricula_provincial: '78901',
        provincia_matricula: 'Buenos Aires',
        cuit_cuil: '20123456789',
        subespecialidad_principal: 'Retina y Vítreo',
        subespecialidad_2: 'Córnea',
        direccion: 'Av. Corrientes 1234',
        localidad: 'CABA',
        provincia: 'Buenos Aires',
        codigo_postal: '1043',
        opcion_pago: 'debito_automatico',
        estado: 'activo',
        documentos: {
          documento_identidad: true,
          documento_matricula: true,
          titulo_medico: true,
          titulo_especialista: true,
          certificado_residencia: false
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR');
  };

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <Icon className="w-5 h-5 text-[#2853B9] mt-0.5" />
      <div className="flex-1">
        <p className="text-sm text-gray-500">{label}</p>
        <p className="font-medium text-gray-900">{value || '-'}</p>
      </div>
    </div>
  );

  const DocumentRow = ({ label, exists }) => (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3">
        <FileText className="w-5 h-5 text-gray-400" />
        <span className="text-gray-700">{label}</span>
      </div>
      {exists ? (
        <div className="flex items-center gap-2">
          <Badge className="bg-green-100 text-green-700">Cargado</Badge>
          <Button variant="ghost" size="sm" className="text-[#2853B9]" disabled>
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <Badge className="bg-gray-100 text-gray-500">No cargado</Badge>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  const totalPendiente = cuotasMockup
    .filter(c => c.estado === 'pendiente')
    .reduce((acc, c) => acc + c.monto, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#2853B9] flex items-center justify-center text-white text-2xl font-bold">
          {socioData?.nombre?.charAt(0) || 'S'}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">
            {socioData?.nombre} {socioData?.apellido}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className="bg-[#2853B9] text-white">{socioData?.categoria_socio}</Badge>
            <Badge className={socioData?.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
              {socioData?.estado === 'activo' ? 'Activo' : 'Pendiente'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="datos" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="datos" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Mis Datos
          </TabsTrigger>
          <TabsTrigger value="documentos" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Documentación
          </TabsTrigger>
          <TabsTrigger value="cuotas" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Cuotas
          </TabsTrigger>
        </TabsList>

        {/* Tab: Mis Datos */}
        <TabsContent value="datos">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-[#2853B9]" />
                  Información Personal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <InfoRow icon={Mail} label="Email" value={socioData?.email} />
                <InfoRow icon={Calendar} label="Fecha de Alta" value={formatDate(socioData?.fecha_alta)} />
                <InfoRow icon={GraduationCap} label="Categoría" value={socioData?.categoria_socio} />
                <InfoRow icon={FileText} label="CUIT/CUIL" value={socioData?.cuit_cuil} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-[#2853B9]" />
                  Información Profesional
                </CardTitle>
              </CardHeader>
              <CardContent>
                <InfoRow icon={FileText} label="Matrícula Nacional" value={socioData?.matricula_nacional} />
                <InfoRow icon={FileText} label="Matrícula Provincial" value={socioData?.matricula_provincial} />
                <InfoRow icon={Building2} label="Provincia Matrícula" value={socioData?.provincia_matricula} />
                <InfoRow icon={GraduationCap} label="Especialidad Principal" value={socioData?.subespecialidad_principal} />
                {socioData?.subespecialidad_2 && (
                  <InfoRow icon={GraduationCap} label="Especialidad Secundaria" value={socioData?.subespecialidad_2} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#2853B9]" />
                  Domicilio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <InfoRow icon={MapPin} label="Dirección" value={socioData?.direccion} />
                <InfoRow icon={Building2} label="Localidad" value={socioData?.localidad} />
                <InfoRow icon={Building2} label="Provincia" value={socioData?.provincia} />
                <InfoRow icon={Mail} label="Código Postal" value={socioData?.codigo_postal} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#2853B9]" />
                  Forma de Pago
                </CardTitle>
              </CardHeader>
              <CardContent>
                <InfoRow 
                  icon={DollarSign} 
                  label="Método de Pago" 
                  value={socioData?.opcion_pago === 'debito_automatico' ? 'Débito Automático' : 'Pago Manual'} 
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Documentación */}
        <TabsContent value="documentos">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#2853B9]" />
                Documentación Presentada
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DocumentRow label="Documento de Identidad (DNI)" exists={socioData?.documentos?.documento_identidad} />
              <DocumentRow label="Matrícula Profesional" exists={socioData?.documentos?.documento_matricula} />
              <DocumentRow label="Título de Médico" exists={socioData?.documentos?.titulo_medico} />
              <DocumentRow label="Título de Especialista" exists={socioData?.documentos?.titulo_especialista} />
              <DocumentRow label="Certificado de Residencia" exists={socioData?.documentos?.certificado_residencia} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Cuotas */}
        <TabsContent value="cuotas">
          <div className="space-y-6">
            {/* Resumen */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-sm text-green-600">Cuotas Pagadas</p>
                      <p className="text-2xl font-bold text-green-700">
                        {cuotasMockup.filter(c => c.estado === 'pagado').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-8 h-8 text-yellow-600" />
                    <div>
                      <p className="text-sm text-yellow-600">Cuotas Pendientes</p>
                      <p className="text-2xl font-bold text-yellow-700">
                        {cuotasMockup.filter(c => c.estado === 'pendiente').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="text-sm text-blue-600">Total Pendiente</p>
                      <p className="text-2xl font-bold text-blue-700">
                        ${totalPendiente.toLocaleString('es-AR')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabla de cuotas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#2853B9]" />
                  Detalle de Cuotas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Período</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Vencimiento</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Monto</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Estado</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Fecha Pago</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cuotasMockup.map((cuota) => (
                        <tr key={cuota.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">{cuota.periodo}</td>
                          <td className="py-3 px-4 text-gray-600">{formatDate(cuota.fecha_vencimiento)}</td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">
                            ${cuota.monto.toLocaleString('es-AR')}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {cuota.estado === 'pagado' ? (
                              <Badge className="bg-green-100 text-green-700">Pagado</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700">Pendiente</Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {cuota.fecha_pago ? formatDate(cuota.fecha_pago) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default MiPerfilSocio;
